import React, { useState, useEffect } from 'react';
import { Star, Quote, Sparkles } from 'lucide-react';
import { testimonials } from '../data/mock';

const Testimonials = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('testimonials-section');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials-section" className="py-24 px-6 relative overflow-hidden section-iridescent">
      {/* Prismatic background effects */}
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-[#FFB800]/15 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#9D4EDD]/15 rounded-full blur-3xl" />
      <div className="absolute top-0 right-1/3 w-80 h-80 bg-[#FF6B9D]/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="section-eyebrow flex items-center justify-center gap-2">
            <Star className="w-4 h-4 text-[#FFB800] fill-[#FFB800]" />
            Customer Stories
            <Star className="w-4 h-4 text-[#FFB800] fill-[#FFB800]" />
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A2E] mb-6">
            Crystal <span className="text-gradient-sunset">Connections</span>
          </h2>
          <p className="text-lg text-[#4A4A6A] max-w-xl mx-auto">
            Hear from our community of healers, seekers, and crystal lovers.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`relative group transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150 + 200}ms` }}
            >
              {/* Card */}
              <div className="relative bg-white rounded-2xl p-8 border border-[#9D4EDD]/10 hover:border-[#9D4EDD]/30 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(157,78,221,0.1),0_0_60px_rgba(255,107,157,0.05)] group-hover:-translate-y-2">
                {/* Quote icon */}
                <div className="absolute -top-4 left-8">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] flex items-center justify-center shadow-lg">
                    <Quote className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-4 pt-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#FFB800] text-[#FFB800]" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-[#1A1A2E] leading-relaxed mb-6 font-serif text-lg italic">
                  "{testimonial.text}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9D4EDD] via-[#FF6B9D] to-[#FFB800] flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-sm">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A2E]">{testimonial.name}</p>
                    <p className="text-sm text-[#7A7A9A]">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
